import { FC, HTMLAttributes, useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface HandleProps extends HTMLAttributes<HTMLDivElement> {
  onDragHandle: (event: MouseEvent) => void;
  start?: boolean;
  sub?: boolean;
  internal?: boolean;
  cursor?: string;
}

export const Handle: FC<HandleProps> = ({ onDragHandle, className, start, sub, internal, cursor }) => {
  const ref = useRef<HTMLDivElement>(null);

  const classNameToUse = twMerge(
    'absolute z-[1000] rounded-md h-full w-2',
    cursor ?? 'cursor-ew-resize',
    sub ? 'bg-white' : 'bg-gray-500',
    className
  );

  const onMouseUp = useCallback(() => {
    window.removeEventListener('mousemove', onDragHandle);
  }, [onDragHandle]);

  const onMouseDown = useCallback(() => {
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
    window.addEventListener('mousemove', onDragHandle);
    window.addEventListener('mouseup', onMouseUp, { once: true });
  }, [onDragHandle, onMouseUp]);

  useEffect(() => {
    const element = ref.current;

    element?.addEventListener('mousedown', onMouseDown);

    return () => {
      element?.removeEventListener('mousedown', onMouseDown);
      element?.removeEventListener('mousemove', onDragHandle);
      element?.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseDown, onMouseUp, onDragHandle, ref]);

  const positionString = internal ? 'calc(100% - 8px)' : 'calc(100% + 1px)';

  return (
    // Interpolated values in tailwind seem to not generate/apply correctly, so use inline style instead
    <div
      ref={ref}
      className={classNameToUse}
      style={{ right: start ? positionString : undefined, left: start ? undefined : positionString }}
    ></div>
  );
};
